import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, MonitorSmartphone, Ticket, Trophy, Crown, Gift, DollarSign, Download, TrendingUp, UserX, Loader2, Save, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, StatCard, Spinner, Field } from "@/components/admin/ui";

const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    onClick={onChange}
    role="switch"
    aria-checked={checked}
    data-testid={testId}
    className="relative w-11 h-6 rounded-full transition-colors"
    style={{ background: checked ? "#00e5ff" : "rgba(148,163,184,0.25)" }}
  >
    <span
      className="absolute top-0.5 w-5 h-5 rounded-full bg-slate-900 transition-transform"
      style={{ transform: checked ? "translateX(22px)" : "translateX(2px)" }}
    />
  </button>
);

const GuestAccessCard = () => {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/public/guest-flags").then(({ data }) => setFlags(data)).catch(() => setFlags({ browse_tournaments: false, browse_leaderboards: false, browse_memberships: false, browse_rewards: false }));
  }, []);

  const toggle = async (key) => {
    if (!flags || saving) return;
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    setSaving(true);
    try {
      await apiClient.put("/admin/guest-flags", { [key]: next[key] });
      toast.success("Saved");
    } catch (e) {
      setFlags(flags);
      toast.error(getErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="guest-access-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,229,255,0.14)", border: "1px solid rgba(0,229,255,0.4)" }}>
          <UserX className="w-5 h-5" style={{ color: "#00e5ff" }} />
        </div>
        <div>
          <h3 className="font-display font-bold text-white uppercase tracking-wide">Guest Access</h3>
          <p className="text-slate-400 text-sm mt-0.5">Guests can always book without an account. Enable additional read-only sections below.</p>
        </div>
      </div>
      {flags == null ? (
        <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading</div>
      ) : (
        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 rounded-xl" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
            <div>
              <p className="text-white font-semibold flex items-center gap-2"><Trophy className="w-4 h-4" style={{ color: "#ffd23f" }} /> Browse Tournaments</p>
              <p className="text-slate-500 text-xs mt-0.5">Guests can view the tournaments list. Signing in is required to register.</p>
            </div>
            <Toggle checked={flags.browse_tournaments} onChange={() => toggle("browse_tournaments")} testId="toggle-guest-tournaments" />
          </label>
          <label className="flex items-center justify-between p-3 rounded-xl" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
            <div>
              <p className="text-white font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4" style={{ color: "#00ff9d" }} /> Browse Leaderboards</p>
              <p className="text-slate-500 text-xs mt-0.5">Guests can see top players. Detailed profiles remain locked.</p>
            </div>
            <Toggle checked={flags.browse_leaderboards} onChange={() => toggle("browse_leaderboards")} testId="toggle-guest-leaderboards" />
          </label>
          <label className="flex items-center justify-between p-3 rounded-xl" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
            <div>
              <p className="text-white font-semibold flex items-center gap-2"><Crown className="w-4 h-4" style={{ color: "#8b5cf6" }} /> Browse Memberships</p>
              <p className="text-slate-500 text-xs mt-0.5">Guests can view membership plans. Signing in is required to subscribe.</p>
            </div>
            <Toggle checked={flags.browse_memberships} onChange={() => toggle("browse_memberships")} testId="toggle-guest-memberships" />
          </label>
          <label className="flex items-center justify-between p-3 rounded-xl" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
            <div>
              <p className="text-white font-semibold flex items-center gap-2"><Gift className="w-4 h-4" style={{ color: "#ff2e97" }} /> Browse Rewards</p>
              <p className="text-slate-500 text-xs mt-0.5">Guests can browse the rewards catalogue. Signing in is required to redeem.</p>
            </div>
            <Toggle checked={flags.browse_rewards} onChange={() => toggle("browse_rewards")} testId="toggle-guest-rewards" />
          </label>
        </div>
      )}
    </div>
  );
};

const PricingSettingsCard = () => {
  const [tiers, setTiers] = useState(null);
  const [party, setParty] = useState(null);
  const [savingTiers, setSavingTiers] = useState(false);
  const [savingParty, setSavingParty] = useState(false);

  useEffect(() => {
    Promise.all([apiClient.get("/admin/multi-unit-tiers"), apiClient.get("/admin/party-booking")])
      .then(([t, p]) => { setTiers(t.data); setParty(p.data); })
      .catch(() => { setTiers([]); setParty({ hourly_rate: 300, min_hours: 1, increment_minutes: 60, enabled: true }); });
  }, []);

  const addTier = () => setTiers([...(tiers || []), { min_stations: 3, discount_percentage: 15 }]);
  const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const setTierField = (i, key, val) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t));

  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      const payload = { tiers: (tiers || []).map((t) => ({ min_stations: Number(t.min_stations), discount_percentage: Number(t.discount_percentage) })) };
      const { data } = await apiClient.put("/admin/multi-unit-tiers", payload);
      setTiers(data);
      toast.success("Multi-unit tiers saved");
    } catch (e) { toast.error(getErrorMessage(e, "Save failed")); } finally { setSavingTiers(false); }
  };

  const saveParty = async () => {
    setSavingParty(true);
    try {
      const payload = {
        first_hour_rate: Number(party.first_hour_rate ?? party.hourly_rate),
        additional_hour_rate: Number(party.additional_hour_rate ?? party.hourly_rate),
        hourly_rate: Number(party.first_hour_rate ?? party.hourly_rate),
        min_hours: Number(party.min_hours),
        increment_minutes: Number(party.increment_minutes),
        enabled: !!party.enabled,
      };
      const { data } = await apiClient.put("/admin/party-booking", payload);
      setParty(data);
      toast.success("Party booking updated");
    } catch (e) { toast.error(getErrorMessage(e, "Save failed")); } finally { setSavingParty(false); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="pricing-settings-card">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,176,32,0.14)", border: "1px solid rgba(255,176,32,0.4)" }}>
          <DollarSign className="w-5 h-5" style={{ color: "#ffb020" }} />
        </div>
        <div>
          <h3 className="font-display font-bold text-white uppercase tracking-wide">Booking Pricing</h3>
          <p className="text-slate-400 text-sm mt-0.5">Multi-station discounts and party (whole-lounge) bookings. Applies to authenticated and guest bookings automatically.</p>
        </div>
      </div>

      {/* Multi-unit tiers */}
      <div className="mb-6">
        <p className="text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">Multi-station auto-discount tiers</p>
        {tiers === null ? (
          <p className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading</p>
        ) : (
          <>
            <div className="space-y-2 mb-3">
              {tiers.length === 0 && <p className="text-slate-500 text-sm">No tiers configured. Add one below.</p>}
              {tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
                  <span className="text-slate-400 text-xs uppercase tracking-wider w-16">Book</span>
                  <input type="number" min="1" className="input-cyber w-24" value={t.min_stations} onChange={(e) => setTierField(i, "min_stations", e.target.value)} data-testid={`multi-tier-${i}-min`} />
                  <span className="text-slate-400 text-xs">stations or more →</span>
                  <input type="number" min="0" max="100" className="input-cyber w-24" value={t.discount_percentage} onChange={(e) => setTierField(i, "discount_percentage", e.target.value)} data-testid={`multi-tier-${i}-disc`} />
                  <span className="text-slate-400 text-xs">% off</span>
                  <button onClick={() => removeTier(i)} className="ml-auto neon-outline-btn px-2 py-1.5 text-xs" style={{ borderColor: "rgba(255,46,151,0.5)", color: "#ff2e97" }} data-testid={`multi-tier-${i}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={addTier} className="neon-outline-btn px-4 py-2" data-testid="multi-tier-add"><Plus className="w-4 h-4" /> Add Tier</button>
              <button onClick={saveTiers} disabled={savingTiers} className="neon-btn px-5 py-2" data-testid="multi-tiers-save">
                {savingTiers ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Tiers</>}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Party booking */}
      <div>
        <p className="text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" style={{ color: "#ff2e97" }} /> Full-lounge party booking</p>
        {party === null ? (
          <p className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading</p>
        ) : (
          <>
            <label className="flex items-center gap-3 mb-3">
              <Toggle checked={!!party.enabled} onChange={() => setParty({ ...party, enabled: !party.enabled })} testId="party-enabled" />
              <span className="text-slate-200">Show the &ldquo;Book the whole lounge&rdquo; option to customers</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="First hour rate ($)">
                <input type="number" min="0" step="1" className="input-cyber" value={party.first_hour_rate ?? party.hourly_rate ?? ""} onChange={(e) => setParty({ ...party, first_hour_rate: e.target.value })} data-testid="party-first-rate" />
              </Field>
              <Field label="Additional hours rate ($/hr)">
                <input type="number" min="0" step="1" className="input-cyber" value={party.additional_hour_rate ?? party.hourly_rate ?? ""} onChange={(e) => setParty({ ...party, additional_hour_rate: e.target.value })} data-testid="party-additional-rate" />
              </Field>
              <Field label="Minimum hours">
                <input type="number" min="0.5" step="0.5" className="input-cyber" value={party.min_hours} onChange={(e) => setParty({ ...party, min_hours: e.target.value })} data-testid="party-min-hours" />
              </Field>
              <Field label="Booking increments (mins)">
                <select className="input-cyber" value={party.increment_minutes} onChange={(e) => setParty({ ...party, increment_minutes: Number(e.target.value) })} data-testid="party-increment">
                  <option value={60} className="bg-slate-900">60 (recommended)</option>
                  <option value={30} className="bg-slate-900">30</option>
                  <option value={15} className="bg-slate-900">15</option>
                </select>
              </Field>
            </div>
            <button onClick={saveParty} disabled={savingParty} className="neon-btn px-5 py-2 mt-3" data-testid="party-save">
              {savingParty ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Party Booking</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function AdminOverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner label="Loading metrics" />;
  if (!stats) return <p className="text-slate-400">Failed to load metrics.</p>;

  const cards = [
    { icon: Users, label: "Total Users", value: stats.total_users, color: "#00e5ff" },
    { icon: Crown, label: "Active Members", value: stats.active_members, color: "#8b5cf6" },
    { icon: Ticket, label: "Total Bookings", value: stats.total_bookings, color: "#00ff9d" },
    { icon: DollarSign, label: "Total Revenue", value: `$${stats.total_revenue.toLocaleString()}`, color: "#ffb020" },
    { icon: TrendingUp, label: "Revenue (This Month)", value: `$${stats.monthly_revenue.toLocaleString()}`, color: "#ff2e97" },
    { icon: MonitorSmartphone, label: "Stations", value: stats.total_stations, color: "#00e5ff" },
    { icon: Trophy, label: "Tournaments", value: `${stats.active_tournaments}/${stats.total_tournaments}`, color: "#ffb020" },
    { icon: Gift, label: "Reward Claims", value: stats.reward_claims, color: "#00ff9d" },
  ];

  return (
    <div>
      <AdminHeader
        title="Admin Overview"
        subtitle="Key metrics across the lounge."
        right={<button onClick={() => navigate("/admin/data-export")} className="neon-outline-btn px-5 py-2.5" data-testid="overview-export"><Download className="w-4 h-4" /> Export Data</button>}
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>
      <div className="mt-8">
        <GuestAccessCard />
      </div>
      <div className="mt-8">
        <PricingSettingsCard />
      </div>
    </div>
  );
}
